import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Save, Upload, Building2, Palette, RotateCcw, Image as ImageIcon } from 'lucide-react';
import api from '../utils/api';

const ConfiguracionGeneral = () => {
  const [config, setConfig] = useState({
    company_name: 'IMAGINA',
    platform_name: 'Sistema de Postventa',
    logo_url: null,
    primary_color: '#9d8272',
    secondary_color: '#c5b8a2',
    accent_color: '#E8E8E7',
    header_bg_color: '#FFFFFF',
    sidebar_bg_color: '#FDFCFB',
    text_primary_color: '#333333',
    text_secondary_color: '#666666'
  });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const logoInputRef = useRef(null);

  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    setLoading(true);
    try {
      const response = await api.get('/configuracion/general');
      if (response.data) {
        setConfig(prev => ({ ...prev, ...response.data }));
      }
    } catch (error) {
      console.error('Error loading config:', error);
      // Use defaults if error
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put('/configuracion/general', config);
      toast.success('Configuración guardada correctamente');
      
      // Apply colors to CSS variables
      applyColors(config);
      
      // Store in localStorage for persistence
      localStorage.setItem('platform_config', JSON.stringify(config));
      
      // Dispatch event for other components to update
      window.dispatchEvent(new CustomEvent('configUpdated', { detail: config }));
    } catch (error) {
      toast.error('Error al guardar la configuración');
    } finally {
      setSaving(false);
    }
  };

  const applyColors = (configData) => {
    const root = document.documentElement;
    root.style.setProperty('--color-primary', configData.primary_color);
    root.style.setProperty('--color-secondary', configData.secondary_color);
    root.style.setProperty('--color-accent', configData.accent_color);
    root.style.setProperty('--color-header-bg', configData.header_bg_color);
    root.style.setProperty('--color-sidebar-bg', configData.sidebar_bg_color);
    root.style.setProperty('--color-text-primary', configData.text_primary_color);
    root.style.setProperty('--color-text-secondary', configData.text_secondary_color);
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const allowedTypes = ['image/jpeg', 'image/png', 'image/svg+xml', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Solo se permiten imágenes JPG, PNG, SVG o WebP');
      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      toast.error('El archivo excede 2MB');
      return;
    }

    setSaving(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await api.post('/configuracion/general/logo', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setConfig(prev => ({ ...prev, logo_url: response.data.logo_url }));
      toast.success('Logo subido correctamente');
    } catch (error) {
      toast.error('Error al subir el logo');
    } finally {
      setSaving(false);
      if (logoInputRef.current) logoInputRef.current.value = '';
    }
  };

  const resetToDefaults = () => {
    if (window.confirm('¿Está seguro de restaurar los valores por defecto?')) {
      setConfig({
        company_name: 'IMAGINA',
        platform_name: 'Sistema de Postventa',
        logo_url: config.logo_url, // Keep current logo
        primary_color: '#9d8272',
        secondary_color: '#c5b8a2',
        accent_color: '#E8E8E7',
        header_bg_color: '#FFFFFF',
        sidebar_bg_color: '#FDFCFB',
        text_primary_color: '#333333',
        text_secondary_color: '#666666'
      });
      toast.info('Valores restaurados. Guarde para aplicar los cambios.');
    }
  };

  const ColorInput = ({ label, value, onChange, description }) => (
    <div className="space-y-2">
      <Label className="text-sm font-medium">{label}</Label>
      <div className="flex items-center gap-3">
        <div 
          className="w-10 h-10 rounded-lg border-2 border-gray-200 cursor-pointer shadow-sm hover:shadow-md transition-shadow"
          style={{ backgroundColor: value }}
          onClick={() => document.getElementById(`color-${label}`)?.click()}
        />
        <Input
          id={`color-${label}`}
          type="color"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-16 h-10 p-1 cursor-pointer"
        />
        <Input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="flex-1 font-mono text-sm"
          placeholder="#000000"
        />
      </div>
      {description && <p className="text-xs text-gray-500">{description}</p>}
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#9d8272]"></div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 max-w-4xl mx-auto space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Configuración General</h1>
          <p className="text-sm text-gray-500">Personaliza la apariencia y datos de la plataforma</p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={resetToDefaults}
            className="border-gray-300"
          >
            <RotateCcw size={16} className="mr-2" />
            Restaurar
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving}
            className="bg-[#9d8272] hover:bg-[#8a7265] text-white"
          >
            <Save size={16} className="mr-2" />
            {saving ? 'Guardando...' : 'Guardar'}
          </Button>
        </div>
      </div>

      {/* Company Info */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Building2 size={20} className="text-[#9d8272]" />
            Información de la Empresa
          </CardTitle>
          <CardDescription>
            Datos básicos que se mostrarán en toda la plataforma
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="company_name">Nombre de la Compañía</Label>
              <Input
                id="company_name"
                value={config.company_name}
                onChange={(e) => setConfig(prev => ({ ...prev, company_name: e.target.value }))}
                placeholder="Nombre de la empresa"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="platform_name">Nombre de la Plataforma</Label>
              <Input
                id="platform_name"
                value={config.platform_name}
                onChange={(e) => setConfig(prev => ({ ...prev, platform_name: e.target.value }))}
                placeholder="Nombre del sistema"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Logo */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <ImageIcon size={20} className="text-[#9d8272]" />
            Logo de la Compañía
          </CardTitle>
          <CardDescription>
            Imagen que se mostrará en el encabezado y documentos
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <div className="w-32 h-32 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center bg-gray-50 overflow-hidden">
              {config.logo_url ? (
                <img 
                  src={config.logo_url} 
                  alt="Logo" 
                  className="max-w-full max-h-full object-contain"
                />
              ) : (
                <div className="text-center text-gray-400">
                  <ImageIcon size={32} className="mx-auto mb-1" />
                  <span className="text-xs">Sin logo</span>
                </div>
              )}
            </div>
            <div className="space-y-2">
              <input
                ref={logoInputRef}
                type="file"
                accept="image/jpeg,image/png,image/svg+xml,image/webp"
                onChange={handleLogoUpload}
                className="hidden"
              />
              <Button
                variant="outline"
                onClick={() => logoInputRef.current?.click()}
                disabled={saving}
                className="border-[#9d8272] text-[#9d8272]"
              >
                <Upload size={16} className="mr-2" />
                {config.logo_url ? 'Cambiar logo' : 'Subir logo'}
              </Button>
              <p className="text-xs text-gray-500">
                Formatos: JPG, PNG, SVG, WebP • Máx. 2MB
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Color Palette */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Palette size={20} className="text-[#9d8272]" />
            Paleta de Colores
          </CardTitle>
          <CardDescription>
            Define los colores de la interfaz de usuario
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Main Colors */}
          <div>
            <h3 className="text-sm font-semibold text-gray-700 mb-4">Colores Principales</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <ColorInput
                label="Color Primario"
                value={config.primary_color}
                onChange={(v) => setConfig(prev => ({ ...prev, primary_color: v }))}
                description="Botones y elementos destacados"
              />
              <ColorInput
                label="Color Secundario"
                value={config.secondary_color}
                onChange={(v) => setConfig(prev => ({ ...prev, secondary_color: v }))}
                description="Bordes y elementos de soporte"
              />
              <ColorInput
                label="Color de Acento"
                value={config.accent_color}
                onChange={(v) => setConfig(prev => ({ ...prev, accent_color: v }))}
                description="Fondos de cards y separadores"
              />
            </div>
          </div>

          {/* Background Colors */}
          <div>
            <h3 className="text-sm font-semibold text-gray-700 mb-4">Colores de Fondo</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <ColorInput
                label="Fondo del Encabezado"
                value={config.header_bg_color}
                onChange={(v) => setConfig(prev => ({ ...prev, header_bg_color: v }))}
                description="Barra superior de navegación"
              />
              <ColorInput
                label="Fondo del Menú Lateral"
                value={config.sidebar_bg_color}
                onChange={(v) => setConfig(prev => ({ ...prev, sidebar_bg_color: v }))}
                description="Panel de navegación izquierdo"
              />
            </div>
          </div>

          {/* Text Colors */}
          <div>
            <h3 className="text-sm font-semibold text-gray-700 mb-4">Colores de Texto</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <ColorInput
                label="Texto Principal"
                value={config.text_primary_color}
                onChange={(v) => setConfig(prev => ({ ...prev, text_primary_color: v }))}
                description="Títulos y texto importante"
              />
              <ColorInput
                label="Texto Secundario"
                value={config.text_secondary_color}
                onChange={(v) => setConfig(prev => ({ ...prev, text_secondary_color: v }))}
                description="Descripciones y texto de apoyo"
              />
            </div>
          </div>

          {/* Preview */}
          <div>
            <h3 className="text-sm font-semibold text-gray-700 mb-4">Vista Previa</h3>
            <div 
              className="border rounded-lg overflow-hidden shadow-sm"
              style={{ backgroundColor: config.sidebar_bg_color }}
            >
              <div 
                className="p-3 border-b flex items-center gap-2"
                style={{ backgroundColor: config.header_bg_color }}
              >
                {config.logo_url && (
                  <img src={config.logo_url} alt="Logo" className="h-8 w-auto" />
                )}
                <span 
                  className="font-bold"
                  style={{ color: config.text_primary_color }}
                >
                  {config.company_name}
                </span>
                <span 
                  className="text-sm"
                  style={{ color: config.text_secondary_color }}
                >
                  | {config.platform_name}
                </span>
              </div>
              <div className="p-4 flex gap-3">
                <button
                  className="px-4 py-2 rounded-md text-white font-medium"
                  style={{ backgroundColor: config.primary_color }}
                >
                  Botón Primario
                </button>
                <button
                  className="px-4 py-2 rounded-md border font-medium"
                  style={{ 
                    borderColor: config.secondary_color,
                    color: config.primary_color
                  }}
                >
                  Botón Secundario
                </button>
                <div 
                  className="px-4 py-2 rounded-md"
                  style={{ backgroundColor: config.accent_color }}
                >
                  <span style={{ color: config.text_secondary_color }}>
                    Elemento de acento
                  </span>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ConfiguracionGeneral;
